import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserMinus, ArrowsClockwise } from '@phosphor-icons/react';

export default function DropoutHub() {
  const [dropouts, setDropouts] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [funds, setFunds] = useState([]);
  const [activeStudents, setActiveStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [showMarkDropout, setShowMarkDropout] = useState(false);
  const [showReallocate, setShowReallocate] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [d, l, f, s] = await Promise.all([
        api.get('/dropouts'), api.get('/dropouts/ledger'), api.get('/dropouts/funds'),
        api.get('/recipients?type=student&status=active')
      ]);
      setDropouts(d.data); setLedger(l.data); setFunds(f.data); setActiveStudents(s.data);
      const all = await api.get('/recipients?type=student');
      setAllStudents(all.data);
    } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  // Mark Dropout
  const MarkDropoutForm = () => {
    const [form, setForm] = useState({ recipient_id: '', reason: '', fund_destination: '' });
    const [saving, setSaving] = useState(false);
    const selectedStudent = activeStudents.find(s => s.id === form.recipient_id);

    const handleSubmit = async () => {
      if (!form.recipient_id || !form.reason || !form.fund_destination) { toast.error('All fields required'); return; }
      setSaving(true);
      try {
        await api.post('/dropouts', form);
        toast.success('Student marked as dropout');
        setShowMarkDropout(false); fetchData();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      setSaving(false);
    };

    return (
      <Dialog open onOpenChange={() => setShowMarkDropout(false)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="mark-dropout-dialog">
          <DialogHeader><DialogTitle className="font-['Chivo']">Mark Student as Dropout</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="data-label">Select Student *</Label>
              <Select value={form.recipient_id} onValueChange={v => setForm({...form, recipient_id: v})}>
                <SelectTrigger className="rounded-sm" data-testid="dropout-student-select"><SelectValue placeholder="Choose student" /></SelectTrigger>
                <SelectContent>{activeStudents.map(s => <SelectItem key={s.id} value={s.id}>{s.name} (Balance: ${(s.wallet_balance || 0).toFixed(2)})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {selectedStudent && (
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <p className="text-sm text-slate-700">Current Balance: <span className="currency font-semibold">${(selectedStudent.wallet_balance || 0).toFixed(2)}</span></p>
                <p className="text-xs text-slate-500 mt-1">This amount will be transferred to the selected dropout fund</p>
              </div>
            )}
            <div><Label className="data-label">Transfer Balance To *</Label>
              <Select value={form.fund_destination} onValueChange={v => setForm({...form, fund_destination: v})}>
                <SelectTrigger className="rounded-sm" data-testid="dropout-fund-select"><SelectValue placeholder="Choose fund" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="westerville">Westerville Fund</SelectItem>
                  <SelectItem value="mcf">MCF Fund</SelectItem>
                  <SelectItem value="iowa">IOWA Fund</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="data-label">Reason *</Label>
              <Textarea value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} className="rounded-sm" rows={3} data-testid="dropout-reason-input" /></div>
            <Button onClick={handleSubmit} disabled={saving} className="w-full bg-[#D13438] hover:bg-[#A4262C] text-white rounded-sm" data-testid="confirm-dropout-btn">
              {saving ? 'Processing...' : 'Confirm Dropout'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Reallocate
  const ReallocateForm = () => {
    const [form, setForm] = useState({ from_fund: '', to_type: '', to_id: '', amount: '', description: '' });
    const [saving, setSaving] = useState(false);
    const sourceFund = funds.find(f => f.fund_id === form.from_fund);

    const handleSubmit = async () => {
      if (!form.from_fund || !form.to_type || !form.amount) { toast.error('Required fields missing'); return; }
      if (form.to_type === 'student' && !form.to_id) { toast.error('Select target student'); return; }
      setSaving(true);
      try {
        await api.post('/dropouts/reallocate', { ...form, amount: parseFloat(form.amount) });
        toast.success('Funds reallocated');
        setShowReallocate(false); fetchData();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      setSaving(false);
    };

    return (
      <Dialog open onOpenChange={() => setShowReallocate(false)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="reallocate-dialog">
          <DialogHeader><DialogTitle className="font-['Chivo']">Reallocate Dropout Funds</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="data-label">Source Fund *</Label>
              <Select value={form.from_fund} onValueChange={v => setForm({...form, from_fund: v})}>
                <SelectTrigger className="rounded-sm" data-testid="realloc-source-select"><SelectValue placeholder="Choose fund" /></SelectTrigger>
                <SelectContent>{funds.map(f => <SelectItem key={f.fund_id} value={f.fund_id}>{f.name} (${(f.balance || 0).toFixed(2)})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="data-label">Destination Type *</Label>
              <Select value={form.to_type} onValueChange={v => setForm({...form, to_type: v, to_id: ''})}>
                <SelectTrigger className="rounded-sm" data-testid="realloc-dest-type"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent><SelectItem value="central_fund">Central Fund Pool</SelectItem><SelectItem value="student">Student</SelectItem></SelectContent>
              </Select>
            </div>
            {form.to_type === 'student' && (
              <div><Label className="data-label">Target Student *</Label>
                <Select value={form.to_id} onValueChange={v => setForm({...form, to_id: v})}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Choose student" /></SelectTrigger>
                  <SelectContent>{activeStudents.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label className="data-label">Amount * {sourceFund && `(Available: $${(sourceFund.balance || 0).toFixed(2)})`}</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="rounded-sm" data-testid="realloc-amount-input" /></div>
            <div><Label className="data-label">Description</Label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-sm" rows={2} /></div>
            <Button onClick={handleSubmit} disabled={saving} className="w-full bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm" data-testid="confirm-realloc-btn">
              {saving ? 'Processing...' : 'Reallocate Funds'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="space-y-6" data-testid="dropout-hub">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900 font-['Chivo']">Dropout Hub</h2>
        <div className="flex gap-2">
          <Button onClick={() => setShowReallocate(true)} className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm text-sm" data-testid="reallocate-btn">
            <ArrowsClockwise size={16} className="mr-1" /> Reallocate Funds
          </Button>
          <Button onClick={() => setShowMarkDropout(true)} className="bg-[#D13438] hover:bg-[#A4262C] text-white rounded-sm text-sm" data-testid="mark-dropout-btn">
            <UserMinus size={16} className="mr-1" /> Mark Dropout
          </Button>
        </div>
      </div>

      {/* Fund Balances */}
      <div className="grid grid-cols-3 gap-4">
        {funds.map(f => (
          <div key={f.fund_id} className="bg-white border border-slate-200 rounded-sm p-4" data-testid={`fund-${f.fund_id}`}>
            <span className="data-label">{f.name} Fund</span>
            <p className="currency text-2xl font-semibold text-slate-900 mt-1">${(f.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            <div className="flex gap-4 mt-1 text-xs text-slate-500">
              <span>In: ${(f.total_in || 0).toFixed(2)}</span>
              <span>Out: ${(f.total_out || 0).toFixed(2)}</span>
            </div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="dropouts">
        <TabsList className="flex border-b border-slate-200 bg-transparent p-0 h-auto">
          <TabsTrigger value="dropouts" className="px-4 py-2 text-sm font-medium text-slate-500 border-b-2 border-transparent rounded-none data-[state=active]:text-[#0F6CBD] data-[state=active]:border-[#0F6CBD] data-[state=active]:bg-transparent data-[state=active]:shadow-none" data-testid="tab-dropouts">Dropouts ({dropouts.length})</TabsTrigger>
          <TabsTrigger value="ledger" className="px-4 py-2 text-sm font-medium text-slate-500 border-b-2 border-transparent rounded-none data-[state=active]:text-[#0F6CBD] data-[state=active]:border-[#0F6CBD] data-[state=active]:bg-transparent data-[state=active]:shadow-none" data-testid="tab-ledger">Dropout Ledger ({ledger.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="dropouts" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <table className="w-full">
              <thead><tr className="bg-slate-50 border-b border-slate-200">
                {['Date', 'Student', 'Reason', 'Fund Destination', 'Balance Transferred', 'Processed By'].map(h => <th key={h} className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-3 px-4">{h}</th>)}
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={6} className="py-6 text-center text-sm text-slate-400">Loading...</td></tr> :
                  dropouts.length === 0 ? <tr><td colSpan={6} className="py-6 text-center text-sm text-slate-400">No dropouts recorded</td></tr> :
                  dropouts.map(d => (
                    <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4 text-sm text-slate-600">{d.dropout_date}</td>
                      <td className="py-3 px-4 text-sm font-medium text-slate-800">{d.recipient_name}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{d.reason}</td>
                      <td className="py-3 px-4"><Badge variant="outline" className="capitalize text-xs">{d.fund_destination}</Badge></td>
                      <td className="py-3 px-4 currency text-sm">${(d.balance_transferred || 0).toFixed(2)}</td>
                      <td className="py-3 px-4 text-xs text-slate-500">{d.created_by_name}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="ledger" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <table className="w-full">
              <thead><tr className="bg-slate-50 border-b border-slate-200">
                {['Date', 'Type', 'Amount', 'From', 'To', 'Reference'].map(h => <th key={h} className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-3 px-4">{h}</th>)}
              </tr></thead>
              <tbody>
                {ledger.length === 0 ? <tr><td colSpan={6} className="py-6 text-center text-sm text-slate-400">No ledger entries</td></tr> :
                  ledger.map(l => (
                    <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4 text-sm text-slate-600">{l.created_at?.substring(0, 10)}</td>
                      <td className="py-3 px-4"><Badge variant="outline" className="capitalize text-xs">{l.type?.replace(/_/g, ' ')}</Badge></td>
                      <td className="py-3 px-4 currency text-sm">${(l.amount || 0).toFixed(2)}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{l.from_entity}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{l.to_entity}</td>
                      <td className="py-3 px-4 text-xs text-slate-500">{l.reference}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {showMarkDropout && <MarkDropoutForm />}
      {showReallocate && <ReallocateForm />}
    </div>
  );
}
